import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  Brush,
  Calculator,
  CalendarDays,
  Clapperboard,
  DraftingCompass,
  FilePen,
  FileText,
  FolderClosed,
  Globe,
  Image as ImageIcon,
  NotebookPen,
  Wallet,
  Sheet,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type { AppId } from '@omnia/shared';
import { tr } from '../lib/i18n.ts';

export interface AppDefinition {
  id: AppId;
  title: string;
  icon: LucideIcon;
  /** CSS custom property holding the app's tile color. */
  tone: string;
  defaultWidth: number;
  defaultHeight: number;
  /** Home position as a fraction of the free desk space, so first-run layouts tile instead of stacking. */
  anchor: { x: number; y: number };
  minWidth: number;
  minHeight: number;
  component: LazyExoticComponent<ComponentType>;
}

export const APPS: Record<AppId, AppDefinition> = {
  files: {
    id: 'files',
    anchor: { x: 0.02, y: 0.04 },
    title: tr.apps.files,
    icon: FolderClosed,
    tone: '--tone-files',
    defaultWidth: 720,
    defaultHeight: 480,
    minWidth: 380,
    minHeight: 300,
    component: lazy(() => import('../apps/files/FilesApp.tsx')),
  },
  notes: {
    id: 'notes',
    anchor: { x: 0.6, y: 0.04 },
    title: tr.apps.notes,
    icon: NotebookPen,
    tone: '--tone-notes',
    defaultWidth: 360,
    defaultHeight: 440,
    minWidth: 280,
    minHeight: 240,
    component: lazy(() => import('../apps/notes/NotesApp.tsx')),
  },
  calculator: {
    id: 'calculator',
    anchor: { x: 0.98, y: 0.92 },
    title: tr.apps.calculator,
    icon: Calculator,
    tone: '--tone-calculator',
    defaultWidth: 312,
    defaultHeight: 456,
    minWidth: 260,
    minHeight: 380,
    component: lazy(() => import('../apps/calculator/CalculatorApp.tsx')),
  },
  calendar: {
    id: 'calendar',
    anchor: { x: 0.32, y: 1 },
    title: tr.apps.calendar,
    icon: CalendarDays,
    tone: '--tone-calendar',
    defaultWidth: 640,
    defaultHeight: 440,
    minWidth: 320,
    minHeight: 380,
    component: lazy(() => import('../apps/calendar/CalendarApp.tsx')),
  },
  kasa: {
    id: 'kasa',
    anchor: { x: 0.18, y: 0.5 },
    title: tr.apps.kasa,
    icon: Wallet,
    tone: '--tone-kasa',
    defaultWidth: 860,
    defaultHeight: 620,
    minWidth: 360,
    minHeight: 420,
    component: lazy(() => import('../apps/kasa/KasaApp.tsx')),
  },
  pdf: {
    id: 'pdf',
    anchor: { x: 0.5, y: 0.3 },
    title: tr.apps.pdf,
    icon: FileText,
    tone: '--tone-pdf',
    defaultWidth: 760,
    defaultHeight: 640,
    minWidth: 420,
    minHeight: 360,
    component: lazy(() => import('../apps/pdf/PdfApp.tsx')),
  },
  image: {
    id: 'image',
    anchor: { x: 0.4, y: 0.2 },
    title: tr.apps.image,
    icon: ImageIcon,
    tone: '--tone-image',
    defaultWidth: 780,
    defaultHeight: 560,
    minWidth: 360,
    minHeight: 320,
    component: lazy(() => import('../apps/image/ImageApp.tsx')),
  },
  docs: {
    id: 'docs',
    anchor: { x: 0.08, y: 0.08 },
    title: tr.apps.docs,
    icon: FilePen,
    tone: '--tone-docs',
    defaultWidth: 860,
    defaultHeight: 680,
    minWidth: 520,
    minHeight: 420,
    component: lazy(() => import('../apps/docs/DocsApp.tsx')),
  },
  sheets: {
    id: 'sheets',
    anchor: { x: 0.92, y: 0.2 },
    title: tr.apps.sheets,
    icon: Sheet,
    tone: '--tone-sheets',
    defaultWidth: 900,
    defaultHeight: 600,
    minWidth: 520,
    minHeight: 380,
    component: lazy(() => import('../apps/sheets/SheetsApp.tsx')),
  },
  cad: {
    id: 'cad',
    anchor: { x: 0.5, y: 0.15 },
    title: tr.apps.cad,
    icon: DraftingCompass,
    tone: '--tone-cad',
    defaultWidth: 960,
    defaultHeight: 660,
    minWidth: 560,
    minHeight: 420,
    component: lazy(() => import('../apps/cad/CadApp.tsx')),
  },
  video: {
    id: 'video',
    anchor: { x: 0.3, y: 0.1 },
    title: tr.apps.video,
    icon: Clapperboard,
    tone: '--tone-video',
    defaultWidth: 1040,
    defaultHeight: 680,
    minWidth: 680,
    minHeight: 480,
    component: lazy(() => import('../apps/video/VideoApp.tsx')),
  },
  ai: {
    id: 'ai',
    anchor: { x: 0.96, y: 0.9 },
    title: tr.apps.ai,
    icon: Sparkles,
    tone: '--tone-ai',
    defaultWidth: 440,
    defaultHeight: 600,
    minWidth: 340,
    minHeight: 380,
    component: lazy(() => import('../apps/ai/AiApp.tsx')),
  },
  notebook: {
    id: 'notebook',
    anchor: { x: 0.85, y: 0.55 },
    title: tr.apps.notebook,
    icon: Brush,
    tone: '--tone-notebook',
    defaultWidth: 720,
    defaultHeight: 560,
    minWidth: 420,
    minHeight: 360,
    component: lazy(() => import('../apps/notebook/NotebookApp.tsx')),
  },
  browser: {
    id: 'browser',
    anchor: { x: 0.7, y: 0.12 },
    title: tr.apps.browser,
    icon: Globe,
    tone: '--tone-browser',
    defaultWidth: 1040,
    defaultHeight: 680,
    minWidth: 360,
    minHeight: 320,
    component: lazy(() => import('../apps/browser/BrowserApp.tsx')),
  },
};
